import * as Form from "@radix-ui/react-form"
import * as VisuallyHidden from "@radix-ui/react-visually-hidden"
import UniversalLink from "library/Loader/UniversalLink"
import { fmobile, fresponsive } from "library/fullyResponsive"
import { useRef, useState } from "react"
import styled, { css } from "styled-components"
import colors from "styles/colors"
import textStyles from "styles/text"
import links from "utils/links"

import useMedia from "library/useMedia"
import CalendlyButton from "./CalendlyButton"
import SecondaryCTA from "./buttons/SecondaryCTA"

export default function EmailInput({
	buttonText,
	className = "",
}: {
	buttonText?: string
	className?: string
}) {
	const [state, setState] = useState<
		"waiting" | "loading" | "success" | "error"
	>("waiting")
	const hiddenButton = useRef<HTMLDivElement>(null)
	const mobile = useMedia(false, false, false, true)

	return (
		<>
			<HiddenButton ref={hiddenButton}>
				<CalendlyButton type="calendly">fake</CalendlyButton>
			</HiddenButton>
			<Wrapper
				onSubmit={(event) => {
					event.preventDefault()

					// make all values strings
					const formData = Object.fromEntries(new FormData(event.currentTarget))
					const email = formData.email
					if (!email) return

					setState("loading")

					fetch("/.netlify/functions/create-card", {
						method: "POST",
						headers: { "Content-Type": "application/x-www-form-urlencoded" },
						body: JSON.stringify({ email }),
					})
						.then((response) => {
							if (response.ok) {
								setState("success")
								hiddenButton.current?.querySelector("button")?.click()
							} else setState("error")
							return response
						})
						.catch((error) => {
							console.error(error)
							setState("error")
						})
				}}
				name="emails"
				data-netlify="true"
				className={className}
			>
				<Form.Field name="email">
					<VisuallyHidden.Root>
						<Form.Label>First Name</Form.Label>
					</VisuallyHidden.Root>
					<Control
						placeholder={
							mobile ? "What's your email?" : "What's your work email?"
						}
						required
						type="email"
					/>
					<Message match="valueMissing">Please enter an email</Message>
					<Message match="typeMismatch">
						Not a valid email, please try again
					</Message>
					{/* forbid dotless domains */}
					<Message match={(v) => !/@.*\./.test(v)}>
						Not a valid email, please try again
					</Message>
					<Background />
				</Form.Field>
				<input type="hidden" name="form-name" value="emails" />

				{state === "success" ? (
					<PretendButton type="button">Sent!</PretendButton>
				) : state === "loading" ? (
					<PretendButton type="button">Sending</PretendButton>
				) : (
					<Form.Submit asChild>
						<SecondaryCTA type="submit">
							{buttonText ?? "Get Started"}
						</SecondaryCTA>
					</Form.Submit>
				)}
				{state === "error" && (
					<PostMessage>
						Something went wrong! Please check your internet connection, or
						contact us at{" "}
						<UniversalLink to={links.contact}>
							contact@tryresponse.com
						</UniversalLink>
					</PostMessage>
				)}
			</Wrapper>
		</>
	)
}

const HiddenButton = styled.div`
	display: none;
`

const Wrapper = styled(Form.Root)`
	${fresponsive(css`
		width: 500px;
		display: grid;
		grid-template-columns: 1fr auto;
		place-items: stretch;
		border-radius: 20px;
		border: 1px solid ${colors.grey1};
		padding: 6px;
		position: relative;
		gap: 5px;
	`)}

	${fmobile(css`
		width: 100%;
	`)}
`

const Control = styled(Form.Control)`
	${textStyles.button1medium};
	${fresponsive(css`
		width: 100%;
		height: 100%;
		padding: 10px;
		color: ${colors.grey3};
		border-radius: 16px;
	`)}

	&:focus {
		outline: none;
	}

	&:not(:placeholder-shown) {
		color: ${colors.grey4};
	}

	&::placeholder {
		color: ${colors.grey2};
	}

	${fmobile(css`
		${textStyles.button2medium}
	`)}
`

const Message = styled(Form.Message)`
	position: absolute;
	left: 18px;
	top: calc(100% + 10px);
	color: ${colors.orange1};
	${textStyles.p3}
`

const Background = styled.div`
	${Control}[data-invalid=true] ~ & {
		background: ${colors.transOrange1};
	}

	${fresponsive(css`
		background: white;
		position: absolute;
		inset: 0;
		z-index: -1;
		border-radius: 20px;
		transition: background 0.2s ease-in-out;
		backdrop-filter: blur(6px);
	`)}
`

const PretendButton = styled(SecondaryCTA)`
	pointer-events: none;
`

const PostMessage = styled.div`
	position: absolute;
	top: calc(100% + 8px);
	left: 0;
	right: 0;
	border-radius: 16px;
	padding: 18px 24px;
	gap: 10px;
	border: 1px solid ${colors.orange3};
	background: ${colors.transOrange1};
	color: ${colors.orange2};
	${textStyles.button1medium};
	backdrop-filter: blur(6px);
	z-index: 100;
	text-align: center;
`
